/**
 * AikoTooltip — drop-in tooltip with the AIKO voice.
 *
 * Replaces shadcn-style `<Tooltip><TooltipTrigger><TooltipContent>` compositions
 * with a single-prop API. The visual treatment auto-adapts to the content via
 * the `tone` heuristic (default `"auto"`):
 *
 *   - `title` set → `"full"`: branded paper card with the AikoMark in the header.
 *   - body is a short string (≤32 chars) and no title → `"minimal"`: light
 *     tooltip shell, no mark, tighter padding. Reads as a label, not a card.
 *   - otherwise (long body, JSX node) → `"full"`: paper card with the mark
 *     inline before the text.
 *
 * The heuristic exists so consumers don't have to think about which variant fits.
 * Brand the act, not the chrome — short button labels stay quiet so substantive
 * AIKO comms stand out. Override with `tone="full"` or `tone="minimal"` when
 * the heuristic guesses wrong.
 *
 * Anatomy:
 *
 *   <AikoTooltip body="Save" />                                   // → minimal
 *   <AikoTooltip body="This will permanently delete the experience." />  // → full
 *   <AikoTooltip title="Save" body="Saves and exits the editor" /> // → full (header)
 *   <AikoTooltip body="Save" tone="full" />                        // pin full
 *
 * Built on Radix Tooltip (not Radix Popover, unlike `<AikoTip>`) — hover-only
 * is the entire use case, and Radix Tooltip ships free `role="tooltip"` /
 * `aria-describedby` wiring plus cross-tooltip delay-skipping via the shared
 * `<TooltipProvider>`.
 *
 * **Requires `<TooltipProvider>` at the app root.** Pretty exports one; mount
 * once near `<App>` and every AikoTooltip inherits the configured delays.
 *
 * For click-to-open helpers with actions, reach for `<AikoTip>` directly.
 *
 * Visual shells (`aikoShell`, `aikoShellTight`) live in `src/lib/aiko-shell.ts`
 * — single source of truth for Aiko silhouettes.
 */
import * as React from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { cn } from "../lib/cn";
import { aikoShell, aikoShellTight } from "../lib/aiko-shell";
import { AikoMark, type AikoMarkProps } from "./AikoMark";
import { useAikoChannel } from "./AikoProvider";

/** Body strings ≤ this length default to `tone="minimal"`. */
const SHORT_TEXT_THRESHOLD = 32;

const isShortString = (n: React.ReactNode): boolean =>
  typeof n === "string" && n.trim().length <= SHORT_TEXT_THRESHOLD;

const resolveTone = (
  tone: AikoTooltipTone | undefined,
  title: string | undefined,
  body: React.ReactNode,
): "minimal" | "full" => {
  if (tone && tone !== "auto") return tone;
  if (title) return "full";
  return isShortString(body) ? "minimal" : "full";
};

/* ============================================================================
 * Types
 * ========================================================================= */

export type AikoTooltipTone = "auto" | "minimal" | "full";

export interface AikoTooltipProps {
  /** The single trigger element. Receives `aria-describedby` automatically. */
  children: React.ReactElement;
  /**
   * Tooltip body. String or small ReactNode. Don't put interactive content
   * here — that's `<AikoTip variant="single" | "rail">`'s job.
   */
  body: React.ReactNode;
  /** Optional bold title above the body. Keep it ≤4 words. Forces `tone="full"`. */
  title?: string;
  /**
   * Visual treatment. Default `"auto"`:
   *   - `title` set → `full`
   *   - body is a short string (≤32 chars) → `minimal`
   *   - otherwise → `full`
   *
   * `full` renders the paper card with the AikoMark; `minimal` renders a light
   * label-style shell with no mark. Pin to override the heuristic.
   */
  tone?: AikoTooltipTone;
  /** Side of the anchor to place the bubble on. Default `"top"`. */
  side?: "top" | "right" | "bottom" | "left";
  /** Alignment along that side. Default `"center"`. */
  align?: "start" | "center" | "end";
  /** Px gap between trigger and bubble. Default 6. */
  sideOffset?: number;
  /** Px nudge along the alignment axis. Default 0. */
  alignOffset?: number;
  /**
   * Hover-open delay in ms. Default 400 — close to industry norms (Linear ≈500,
   * GitHub ≈400, MUI 100, shadcn 700). Pass `0` to open instantly.
   */
  delayDuration?: number;
  /** Controlled open state. */
  open?: boolean;
  /** Uncontrolled initial open state. */
  defaultOpen?: boolean;
  /** Fires on open / close. */
  onOpenChange?: (open: boolean) => void;
  /** className override on the bubble. */
  className?: string;
  /** AikoMark size. Default `"xs"` — tooltips run tighter than info cards. Ignored in `tone="minimal"`. */
  markSize?: AikoMarkProps["size"];
  /** Hide the AikoMark in `tone="full"`. Rare — use only if the trigger already shows it. */
  hideMark?: boolean;
  /**
   * Opt this tooltip into the anti-intrusion contract. When set + wrapped in
   * `<AikoProvider>`, repeated dismisses snooze the channel.
   * See [`docs/contracts/aiko-intrusion.md`](../../docs/contracts/aiko-intrusion.md).
   */
  channelId?: string;
}

/* ============================================================================
 * Component
 * ========================================================================= */

export const AikoTooltip = React.forwardRef<HTMLDivElement, AikoTooltipProps>(
  function AikoTooltip(
    {
      children,
      body,
      title,
      tone,
      side = "top",
      align = "center",
      sideOffset = 6,
      alignOffset = 0,
      delayDuration = 400,
      open,
      defaultOpen,
      onOpenChange,
      className,
      markSize = "xs",
      hideMark,
      channelId,
    },
    ref,
  ) {
    const channel = useAikoChannel(channelId ?? "");
    const isManaged = !!channelId;
    const channelGate = isManaged ? channel.canShow : true;

    const handleOpenChange = (next: boolean) => {
      if (next && !channelGate) return;
      if (!next && isManaged) channel.dismiss();
      onOpenChange?.(next);
    };

    // When the channel forbids showing, force the tooltip closed regardless of
    // controlled / uncontrolled state.
    const effectiveOpen =
      isManaged && !channelGate && open !== false ? false : open;
    const effectiveDefaultOpen =
      isManaged && !channelGate ? false : defaultOpen;

    const effectiveTone = resolveTone(tone, title, body);

    return (
      <Tooltip.Root
        open={effectiveOpen}
        defaultOpen={effectiveDefaultOpen}
        onOpenChange={handleOpenChange}
        delayDuration={delayDuration}
      >
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            ref={ref}
            side={side}
            align={align}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            className={cn(
              effectiveTone === "minimal" ? aikoShellTight : aikoShell,
              effectiveTone === "minimal" ? null : "max-w-[260px]",
              className,
            )}
          >
            {effectiveTone === "minimal" ? (
              <div className="px-2.5 py-1 text-[11.5px] leading-[1.5] text-foreground">
                {body}
              </div>
            ) : title ? (
              <div className="px-3.5 py-2.5">
                <div className="mb-1 flex items-center gap-2">
                  {hideMark ? null : <AikoMark size={markSize} />}
                  <div className="text-[12.5px] font-semibold leading-tight text-foreground">
                    {title}
                  </div>
                </div>
                <div className="text-[11.5px] leading-[1.5] text-muted-foreground">
                  {body}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2">
                {hideMark ? null : <AikoMark size={markSize} />}
                <div className="text-[11.5px] leading-[1.5] text-foreground">
                  {body}
                </div>
              </div>
            )}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  },
);
AikoTooltip.displayName = "AikoTooltip";
